import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useActions } from "../../../../../hooks/useActions";
import { useTypedSelector } from "../../../../../hooks/useTypedSelector";
import { CustomCheckbox, CustomSelect } from "../../../../../share/Input/Input";
import FilterButtonWithModal from "../../../../../share/filters/FilterButtonWithModal";
import {
  CheckboxList,
  FilterSection,
  FilterSections,
} from "../../../../../share/filters";
import { FilterTitleWithInfo } from "../../../../../share/filters/FilterTitleWithInfo";
import { FilterIdsEnum } from "../../../../../store/reducers/_maps/main/mapFilters/types";
import styles from "./OtherFilters.module.scss";

interface IServiceSelectItem {
  id: string;
  value: string;
  isPlaceholder?: boolean;
}

const PLACEHOLDER_VALUE = "1";

const OtherFilters = () => {
  const filterId: FilterIdsEnum = "cybersecurityOtherFilters";
  const { company_type, services, other_services } = useTypedSelector(
    (state) => state.mapCybersecurityInfo.params
  );
  const {
    company_type: selectedCompanyType,
    services: selectedServices,
    other_services: selectedOtherServices = [],
    certifications,
  } = useTypedSelector((state) => state.cybersecurityMapFilters);
  const {
    toggleCybersecurityCompanyType,
    toggleCybersecurityOtherServices,
    cleanCybersecurityCompanyType,
    cleanCybersecurityServices,
    // setCybersecurityOtherServices,
    cleanCybersecurityOtherServices,
    setCybersecurityCertifications,
    cleanCybersecurityCertifications,
    toggleCybersecurityServices,
    applyFilter,
  } = useActions();

  const handleCompanyTypeChange = (value: string) => {
    toggleCybersecurityCompanyType(value);
    applyFilter();
  };

  // const handleOtherServicesChange = () => {
  //   setCybersecurityOtherServices(!other_services);
  //   applyFilter();
  // };

  const handleCertificationsChange = () => {
    setCybersecurityCertifications(!certifications);
    applyFilter();
  };

  const handleClean = () => {
    cleanCybersecurityCompanyType([]);
    cleanCybersecurityServices([]);
    cleanCybersecurityOtherServices([]);
    cleanCybersecurityCertifications([]);
  };

  const serviceOptions = useMemo(() => {
    const options = services ? [...services] : [];

    selectedServices.forEach((value) => {
      if (!options.includes(value)) {
        options.push(value);
      }
    });

    return options.sort((a, b) => a.localeCompare(b));
  }, [services, selectedServices]);

  const hasAvailableServices = useMemo(
    () => serviceOptions.some((option) => !selectedServices.includes(option)),
    [serviceOptions, selectedServices]
  );

  const serviceSelectItems = useMemo<IServiceSelectItem[]>(() => {
    const items = selectedServices.map((value) => ({
      id: value,
      value,
    }));

    if (
      serviceOptions.length &&
      (!selectedServices.length || hasAvailableServices)
    ) {
      items.push({
        id: "placeholder",
        value: PLACEHOLDER_VALUE,
      });
    }

    return items;
  }, [selectedServices, serviceOptions, hasAvailableServices]);

  const handleServiceSelectChange = (
    item: IServiceSelectItem,
    value: string
  ) => {
    if (value === PLACEHOLDER_VALUE) {
      return;
    }

    if (item.isPlaceholder) {
      if (!selectedServices.includes(value)) {
        toggleCybersecurityServices(value);
        applyFilter();
      }
      return;
    }

    if (item.value === value || selectedServices.includes(value)) {
      return;
    }

    toggleCybersecurityServices(item.value);
    toggleCybersecurityServices(value);
    applyFilter();
  };

  const handleRemoveService = (item: IServiceSelectItem) => {
    if (item.isPlaceholder) {
      return;
    }

    if (selectedServices.includes(item.value)) {
      toggleCybersecurityServices(item.value);
      applyFilter();
    }
  };

  const handleOtherServiceChange = (value: string) => {
    toggleCybersecurityOtherServices(value);
    applyFilter();
  };

  const { t } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.cybersecurity.otherFilter",
  });

  const { t: tMapFilters } = useTranslation("translation", {
    keyPrefix: "mapUI.filters",
  });

  const noFilterOptions = tMapFilters("noFilterOptions", "no filter options");
  const servicesLabel = t("services");
  const chooseServiceLabel = t("chooseService", {
    defaultValue: servicesLabel,
  });
  const otherServicesTitle = t("otherServicesTitle", {
    defaultValue: t("otherServices"),
  });

  const title = t("title");
  const filterCount =
    selectedCompanyType.length +
    selectedServices.length +
    (selectedOtherServices.length ? 1 : 0) +
    (certifications ? 1 : 0);
  const pinText = filterCount > 0 && filterCount;

  return (
    <FilterButtonWithModal
      title={title}
      filterId={filterId}
      pinText={pinText}
      onClear={handleClean}
      width={512}
    >
      <FilterSections>
        <FilterSection>
          <FilterTitleWithInfo title={t("services")} />
          {serviceSelectItems.length ? (
            <div className={styles.servicesSelectWrapper}>
              {serviceSelectItems.map((item, index) => (
                <CustomSelect
                  key={item.id}
                  value={item.value}
                  name={`service-select-${index}`}
                  classSelectName={styles.select}
                  onChange={(e) =>
                    handleServiceSelectChange(item, e.target.value)
                  }
                  onClick={() => handleRemoveService(item)}
                >
                  <option value={PLACEHOLDER_VALUE} hidden disabled>
                    {chooseServiceLabel}
                  </option>
                  {serviceOptions.map((option) => (
                    <option
                      key={option}
                      value={option}
                      disabled={
                        item.value !== option &&
                        selectedServices.includes(option)
                      }
                    >
                      {option}
                    </option>
                  ))}
                </CustomSelect>
              ))}
            </div>
          ) : (
            <div>{noFilterOptions}</div>
          )}
        </FilterSection>

        <FilterSection>
          <FilterTitleWithInfo title={t("companyType")} />
          <CheckboxList>
            {company_type?.length &&
              company_type.sort().map((value: string) => (
                <CustomCheckbox
                  value={value}
                  key={value}
                  checked={selectedCompanyType.includes(value)}
                  onChange={() => handleCompanyTypeChange(value)}
                  // onClick={() => handleCompanyTypeChange(value)}
                >
                  {value}
                </CustomCheckbox>
              ))}
          </CheckboxList>
        </FilterSection>

        {/*  */}

        <FilterSection>
          <FilterTitleWithInfo title={otherServicesTitle} />
          <CheckboxList>
            {other_services?.length > 0 &&
              other_services.map((other_service) => (
                <CustomCheckbox
                  value={other_service}
                  key={other_service}
                  checked={selectedOtherServices.includes(other_service)}
                  onChange={() => handleOtherServiceChange(other_service)}
                >
                  {other_service}
                </CustomCheckbox>
              ))}
          </CheckboxList>
        </FilterSection>

        <FilterSection>
          <FilterTitleWithInfo title={t("certifications")} />
          <CheckboxList>
            <CustomCheckbox
              value={t("yes")}
              checked={!!certifications}
              onChange={handleCertificationsChange}
              onClick={handleCertificationsChange}
            >
              {t("yes")}
            </CustomCheckbox>
          </CheckboxList>
        </FilterSection>
      </FilterSections>
    </FilterButtonWithModal>
  );
};

export default OtherFilters;
